import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDownIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import AiCommentArrow from '../assets/icons/ai-comment-arrow.svg';
import AiFactPositive from '../assets/icons/ai-fact-positive.svg';
import AiFactNegative from '../assets/icons/ai-fact-negative.svg';
import toast from 'react-hot-toast';
import ResponseActionModal from './components/ResponseActionModal';
import HHResumeModal from './components/HHResumeModal';
import AvitoResumeModal from './components/AvitoResumeModal';
import CampaignCheckResultsModal from './CampaignCheckResultsModal';
import { useBackgroundResumeLoader } from './hooks/useBackgroundResumeLoader';
import {
  applyAvitoApplicationAction,
  getAvitoApplicationStates,
  markAvitoApplicationViewed,
  markVacancyResponseViewed,
  checkCampaignCandidates,
  openHHResumeContacts,
} from './services/api';
import { createCampaign, checkActiveConversations } from '../services/aiRecruiterService';
import { useEstaffLinkGuard } from '../hooks/useEstaffLinkGuard';
import { useAuthStore } from '../store/authStore';
import { useVacancyStore } from '../store/vacancyStore';
import { buildRelevanceFields, evaluationToRelevanceInput } from '../sources/relevance';
import { loadLatestCampaignReusableSettings } from './utils/campaignSettings';
import {
  findAvitoMaybeSuitableStatus,
  getAvitoApplicationId,
  getAvitoChatId,
  getAvitoPhone,
  getAvitoResumeId,
} from './utils/avitoResponsesCampaign';
import {
  buildClarifyExperienceCampaignData,
  buildClarifyExperienceCandidate,
  hasClarifyCandidateContact,
  isOpenedContactsReadyForClarify,
  MAILINGS_NAV_STATE_KEY,
} from '../constants/clarifyExperience';
import { formatCandidateShortName } from './utils/candidatePrivacy';

interface VacancyResponsesInlineProps {
  vacancyId: string;
  selectedVacancy?: any;
  responses: any[];
  aiEvaluations: Record<string, any>;
  evaluatingIds: Set<string>;
  queuedEvaluations?: Record<string, boolean>;
  onEvaluateResume?: (vacancyId: string, responseId: string, resumeData: any) => Promise<void>;
  onRefresh: () => void | Promise<void>;
  onRemoveResponses?: (responseIds: string[]) => void;
  onRefreshVacancies?: () => void | Promise<void>;
  selectedResponses?: Set<string>;
  onSelectedResponsesChange?: (selected: Set<string>) => void;
  scoringCreditLimitHit?: boolean;
}

function sameBooleanMap(left: Record<string, boolean>, right: Record<string, boolean>) {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
}

const VacancyResponsesInline: React.FC<VacancyResponsesInlineProps> = ({
  vacancyId,
  selectedVacancy,
  responses,
  aiEvaluations,
  evaluatingIds,
  queuedEvaluations,
  onEvaluateResume,
  onRefresh,
  onRemoveResponses,
  selectedResponses: externalSelectedResponses,
  onSelectedResponsesChange,
  scoringCreditLimitHit,
}) => {
  const navigate = useNavigate();
  const [internalSelectedResponses, setInternalSelectedResponses] = useState<Set<string>>(new Set());
  
  const selectedResponses = externalSelectedResponses ?? internalSelectedResponses;
  const setSelectedResponses = (value: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    const newValue = typeof value === 'function' ? value(selectedResponses) : value;
    if (onSelectedResponsesChange) {
      onSelectedResponsesChange(newValue);
    } else {
      setInternalSelectedResponses(newValue);
    }
  };
  const [expandedLastJob, setExpandedLastJob] = useState<Record<string, boolean>>({});
  const [expandedAiComment, setExpandedAiComment] = useState<Record<string, boolean>>({});
  const [actionModalResponses, setActionModalResponses] = useState<any[]>([]);
  const [hhResumeModalOpen, setHhResumeModalOpen] = useState(false);
  const [selectedHHResume, setSelectedHHResume] = useState<any>(null);
  const [avitoResumeModalOpen, setAvitoResumeModalOpen] = useState(false);
  const [selectedAvitoResume, setSelectedAvitoResume] = useState<any>(null);
  const prevEvaluationKeysRef = useRef<Set<string>>(new Set());
  const [localQueuedEvaluations, setLocalQueuedEvaluations] = useState<Record<string, boolean>>({});
  const evaluationRunIdRef = useRef(0);
  const requestedEvaluationKeysRef = useRef<Set<string>>(new Set());
  const evalRetryAtRef = useRef<Record<string, number>>({});
  const evalRetryCountRef = useRef<Record<string, number>>({});
  const evalErrorSeenRef = useRef<Record<string, boolean>>({});
  const [clarifyingIds, setClarifyingIds] = useState<Set<string>>(new Set());
  const [singleCheckResultsModalOpen, setSingleCheckResultsModalOpen] = useState(false);
  const [singleCheckResult, setSingleCheckResult] = useState<any>(null);
  const [singleCheckResultMessage, setSingleCheckResultMessage] = useState<string | null>(null);
  const [pendingSingleCampaign, setPendingSingleCampaign] = useState<any>(null);
  const [activeResumeIds, setActiveResumeIds] = useState<Set<string>>(new Set());
  const markedViewedRef = useRef<Set<string>>(new Set());
  // TASKNEIROKLYUCH-435 FR-1: guard-хук для проверки привязки к E-Staff вакансии
  const { checkEstaffLinkStatus, guardModal: estaffLinkGuardModal } = useEstaffLinkGuard();
  const scoringLimitMessage = 'Количество доступных оценок резюме исчерпано. Запросите расширение доступа у администратора.';

  useEffect(() => {
    const resumeIds = responses
      .filter((r) => (r.sourceType || r.source || '').toString().toLowerCase() !== 'avito')
      .map((r) => r.resume?.id)
      .filter((id): id is string => !!id);
    if (resumeIds.length === 0) return;

    let cancelled = false;
    checkActiveConversations(resumeIds)
      .then((ids) => {
        if (!cancelled) setActiveResumeIds(new Set(ids));
      })
      .catch((err) => {
        console.warn('Failed to check active conversations:', err);
      });
    return () => { cancelled = true; };
  }, [responses]);

  const markViewed = async (response: any) => {
    const source = (response?.sourceType || response?.source || '').toString().toLowerCase();
    const idRaw = response?.id ?? response?.application_id;
    const id = idRaw != null ? String(idRaw) : '';
    if (!id) return;
    const dedupeKey = `${source || 'hh'}:${id}`;
    if (markedViewedRef.current.has(dedupeKey)) return;
    markedViewedRef.current.add(dedupeKey);
    try {
      if (source === 'avito') {
        await markAvitoApplicationViewed(id, true);
        return;
      }
      const responseVacancyId = response?.sourceVacancyId || vacancyId;
      if (!responseVacancyId) return;
      await markVacancyResponseViewed(String(responseVacancyId), id);
    } catch {
    }
  };

  const handleShowHHResume = (response: any, resume: any) => {
    const id = resume?.id;
    if (!id) {
      toast.error('Не удалось открыть резюме');
      return;
    }
    void markViewed(response);
    setSelectedHHResume(resume);
    setHhResumeModalOpen(true);
  };

  const handleShowResume = (response: any, resume: any) => {
    const source = (response?.sourceType || response?.source || resume?.source || '').toString().toLowerCase();
    if (source === 'avito') {
      if (!resume?.id) {
        toast.error('Не удалось открыть резюме');
        return;
      }
      void markViewed(response);
      const contacts = response?.contacts;
      const hasApplicationContacts = Boolean(contacts?.chat?.value || contacts?.phones?.length);
      setSelectedAvitoResume({
        ...resume,
        source: 'avito',
        contacts,
        canViewContacts: resume?.canViewContacts ?? hasApplicationContacts,
      });
      setAvitoResumeModalOpen(true);
      return;
    }
    handleShowHHResume(response, resume);
  };

  const finalizeSingleClarifyCampaign = async (pending: any) => {
    const created = await createCampaign(pending.campaignData, pending.requestConfig);

    setActiveResumeIds(prev => new Set(prev).add(pending.resumeId));
    toast.success('Рассылка создана. Проверьте настройки и нажмите «Запустить».');

    navigate('/recruiter/mailings', {
      state: {
        [MAILINGS_NAV_STATE_KEY]: {
          campaignId: String(created.id),
          activeTab: 'settings',
        },
      },
    });
  };

  const handleSingleCheckCancel = () => {
    setSingleCheckResultsModalOpen(false);
    setSingleCheckResult(null);
    setSingleCheckResultMessage(null);
    setPendingSingleCampaign(null);
  };

  const handleSingleCheckConfirm = async () => {
    if (!pendingSingleCampaign) return;
    const pending = pendingSingleCampaign;
    setClarifyingIds(prev => new Set(prev).add(pending.responseId));
    try {
      await finalizeSingleClarifyCampaign(pending);
      handleSingleCheckCancel();
    } catch (error: any) {
      console.error('Error creating clarify experience campaign:', error);
      toast.error(error?.response?.data?.message || 'Ошибка при создании рассылки');
    } finally {
      setClarifyingIds(prev => {
        const next = new Set(prev);
        next.delete(pending.responseId);
        return next;
      });
    }
  };

  const applyAvitoMaybeSuitableStatus = async (applicationId: string) => {
    try {
      const states = await getAvitoApplicationStates();
      const action = findAvitoMaybeSuitableStatus(Array.isArray(states) ? states : []);
      if (!action) {
        toast('Рассылка создана, но статус не изменён: в Avito-статусах не найдено «Может подойти».', { icon: '⚠️' });
        return;
      }
      await applyAvitoApplicationAction([applicationId], action);
      await onRefresh?.();
    } catch (error: any) {
      toast(error?.response?.data?.message || 'Рассылка создана, но статус Avito-отклика не изменился.', { icon: '⚠️' });
    }
  };

  const handleAvitoClarifyExperience = async (response: any) => {
    const resume = response.resume || {};
    const resumeId = getAvitoResumeId(response);
    const applicationId = getAvitoApplicationId(response);

    if (!resumeId || !applicationId) {
      toast.error('Не удалось получить ID Avito-отклика');
      return;
    }

    setClarifyingIds(prev => new Set(prev).add(response.id));

    try {
      const chatId = getAvitoChatId(response);
      if (!chatId) {
        toast.error('У Avito-отклика нет chat_id. Рассылка не создана.');
        return;
      }
      const phone = getAvitoPhone(response);

      const user = useAuthStore.getState().user;
      const tenantUserId = user?.id;
      const recruiterName = user?.firstName || 'Рекрутер';
      const positionId = useVacancyStore.getState().selectedVacancyId;
      const vacancyTitle = selectedVacancy?.title || resume.title || 'Вакансия';
      const vacancyDescription = selectedVacancy?.description || '';
      const vacancyCompanyDescription = selectedVacancy?.companyDescription || '';
      const avitoVacancyId = (selectedVacancy as any)?.avitoVacancyId || vacancyId || undefined;

      const responseVacancyId = response?.sourceVacancyId || avitoVacancyId;
      const evaluation = responseVacancyId ? aiEvaluations[`${responseVacancyId}_${response.id}`] : undefined;
      const relevanceInput = evaluationToRelevanceInput(evaluation);
      const relevanceFields = relevanceInput ? buildRelevanceFields(relevanceInput) : {};

      const { candidate, candidateName } = buildClarifyExperienceCandidate({
        source: 'avito',
        resume: {
          ...resume,
          id: resumeId,
          avitoChatId: chatId,
        },
        campaignCandidate: {
          avitoChatId: chatId,
          phone,
        },
        response,
        responseId: applicationId,
        avitoChatId: chatId,
        avitoApplicationId: applicationId,
        resumeUrl: resume.resumeUrl || resume.url || resume.alternate_url,
        relevanceFields,
      });
      if (!hasClarifyCandidateContact(candidate)) {
        toast.error('У Avito-отклика нет chat_id. Рассылка не создана.');
        return;
      }

      const campaignData = buildClarifyExperienceCampaignData({
        source: 'avito',
        candidates: [candidate],
        vacancyTitle,
        vacancyDescription,
        vacancyCompanyDescription,
        avitoVacancyId,
        positionId: positionId || undefined,
        recruiterName,
        recruiterId: tenantUserId ? String(tenantUserId) : undefined,
        aiScreeningEnabled: Boolean((selectedVacancy as any)?.aiScreeningEnabled),
        hrInterviewEnabled: (selectedVacancy as any)?.hrInterviewEnabled !== false,
        campaignName: `Уточнение опыта: ${candidateName}`,
      });

      const requestConfig = user?.companyId ? { companyId: user.companyId } : undefined;
      const created = await createCampaign(campaignData, requestConfig);
      await applyAvitoMaybeSuitableStatus(applicationId);
      setActiveResumeIds(prev => new Set(prev).add(resumeId));
      toast.success('Рассылка создана. Проверьте настройки и нажмите «Запустить».');

      navigate('/recruiter/mailings', {
        state: {
          [MAILINGS_NAV_STATE_KEY]: {
            campaignId: String(created.id),
            activeTab: 'settings',
          },
        },
      });
    } catch (error: any) {
      console.error('Error creating Avito clarify experience campaign:', error);
      toast.error(error?.response?.data?.message || 'Ошибка при создании рассылки');
    } finally {
      setClarifyingIds(prev => {
        const next = new Set(prev);
        next.delete(response.id);
        return next;
      });
    }
  };

  // TASKNEIROKLYUCH-465: создаём DRAFT-рассылку и переводим рекрутера в окно
  // настроек — там он редактирует первое сообщение и нажимает «Запустить».
  const handleClarifyExperience = async (response: any) => {
    const resume = response.resume || {};
    const fullResume = fullResumes[resume.id];
    const resumeId = resume.id;

    if (!resumeId) {
      toast.error('Не удалось получить ID резюме кандидата');
      return;
    }

    setClarifyingIds(prev => new Set(prev).add(response.id));

    try {
      const user = useAuthStore.getState().user;
      const tenantUserId = user?.id;
      // Только имя (без фамилии): подставляется как {{recruiter_name}} в первое
      // сообщение и в поле «Имя AI-рекрутера» настроек рассылки. В холодном поиске
      // recruiterName уходит пустым и бэк подтягивает короткое имя из настроек
      // мессенджер-аккаунта — тут оставляем тот же стиль («Виктор», не «Виктор Феско»).
      const recruiterName = user?.firstName || 'Рекрутер';

      const vacancyTitle = selectedVacancy?.title || resume.title || 'Вакансия';
      const vacancyDescription = selectedVacancy?.description || '';
      const vacancyCompanyDescription = selectedVacancy?.companyDescription || '';
      const positionId = useVacancyStore.getState().selectedVacancyId;

      // Проверяем E-Staff source-link без открытия отдельной модалки.
      // Предупреждение имеет смысл только если E-Staff включён у компании.
      const estaffLink = await checkEstaffLinkStatus(positionId);
      const estaffVacancyId = estaffLink.estaffVacancyId;
      const estaffMissingMessage = estaffLink.estaffEnabled && !estaffVacancyId
        ? 'Для этой позиции не выбрана вакансия E-Staff. Контакты открыты в HH, но кандидат не будет сохранён в E-Staff.'
        : null;
      let openedFullResume = fullResume;
      let campaignCandidate: any = null;
      try {
        const opened = await openHHResumeContacts(resumeId, {
          canViewContacts: resume.canViewContacts,
          vacancyId: estaffVacancyId || undefined,
        });
        openedFullResume = opened?.resume || opened?.data || openedFullResume;
        campaignCandidate = opened?.campaignCandidate || opened?.data?.campaignCandidate || null;
        const openedHasContacts = Boolean(
          opened?.hasContacts
          || (Array.isArray(openedFullResume?.contact) && openedFullResume.contact.length > 0),
        );
        if (!openedHasContacts) {
          toast.error('У отклика нет телефона или email. Рассылка не создана.');
          return;
        }
        if (!isOpenedContactsReadyForClarify(opened, estaffVacancyId)) {
          toast.error('Не удалось поставить отклик в очередь синхронизации E-Staff. Рассылка не создана.');
          return;
        }
      } catch (_err) {
        toast.error('Не удалось открыть контакты отклика. Рассылка не создана.');
        return;
      }

      // Снапшот AI-оценки отклика: API возвращает плоскую структуру
      // { rating, comment, positiveFacts, negativeFacts }. evaluationToRelevanceInput
      // соберёт тот же объект aiComment, что приходит в холодном поиске,
      // чтобы в правом сайдбаре диалога рисовались плюсы/минусы (TASKNEIROKLYUCH-728).
      const responseVacancyId = response?.sourceVacancyId || vacancyId;
      const evaluation = aiEvaluations[`${responseVacancyId}_${response.id}`];
      const relevanceInput = evaluationToRelevanceInput(evaluation);
      const relevanceFields = relevanceInput ? buildRelevanceFields(relevanceInput) : {};

      const { candidate, candidateName } = buildClarifyExperienceCandidate({
        resume,
        fullResume: openedFullResume,
        campaignCandidate,
        response,
        responseId: response.id,
        relevanceFields,
      });
      if (!hasClarifyCandidateContact(candidate)) {
        toast.error('У отклика нет телефона или email. Рассылка не создана.');
        return;
      }

      // TASKNEIROKLYUCH-434: проверяем статус кандидата в e-staff и «писали ли мы ему
      // в последний месяц» перед началом clarify-experience диалога. Если занят —
      // показываем причину и не стартуем кампанию.
      const companyId = user?.companyId || null;
      let candidateCheckResult: any = { total: 1, available: 1, blocked: [] };
      try {
        const result = await checkCampaignCandidates(
          [
            {
              name: candidateName,
              phone: candidate.messengerContacts?.phone || null,
              email: candidate.email || candidate.messengerContacts?.email || null,
              hhResumeId: candidate.messengerContacts?.hhResumeId || null,
            },
          ],
          companyId,
          positionId,
        );
        candidateCheckResult = {
          total: result?.total ?? 1,
          available: result?.available ?? 1,
          blocked: result?.blocked ?? [],
        };
        if (result && Array.isArray(result.blocked) && result.blocked.length > 0) {
          const reason = result.blocked[0]?.reason || 'кандидат сейчас в активном процессинге';
          toast.error(`Нельзя начать диалог: ${reason}`);
          return;
        }
      } catch (err) {
        toast('Не удалось проверить статус в E-Staff. Диалог будет создан без проверки.', { icon: '⚠️' });
      }

      let campaignSettings = {};
      try {
        campaignSettings = await loadLatestCampaignReusableSettings(positionId);
      } catch (error) {
        console.warn('Failed to load previous campaign settings, using defaults', error);
      }

      const campaignData = buildClarifyExperienceCampaignData({
        candidates: [candidate],
        vacancyTitle,
        vacancyDescription,
        vacancyCompanyDescription,
        hhVacancyId: vacancyId || undefined,
        estaffVacancyId,
        positionId: positionId || undefined,
        recruiterName,
        recruiterId: tenantUserId ? String(tenantUserId) : undefined,
        aiScreeningEnabled: Boolean((selectedVacancy as any)?.aiScreeningEnabled),
        hrInterviewEnabled: (selectedVacancy as any)?.hrInterviewEnabled !== false,
        campaignSettings,
        campaignName: `Уточнение опыта: ${candidateName}`,
      });

      const requestConfig = companyId ? { companyId } : undefined;
      const pendingCampaign = {
        responseId: response.id,
        resumeId,
        campaignData,
        requestConfig,
      };
      if (estaffMissingMessage) {
        setSingleCheckResult(candidateCheckResult);
        setSingleCheckResultMessage(estaffMissingMessage);
        setPendingSingleCampaign(pendingCampaign);
        setSingleCheckResultsModalOpen(true);
        return;
      }

      await finalizeSingleClarifyCampaign(pendingCampaign);
    } catch (error: any) {
      console.error('Error creating clarify experience campaign:', error);
      toast.error(error?.response?.data?.message || 'Ошибка при создании рассылки');
    } finally {
      setClarifyingIds(prev => {
        const next = new Set(prev);
        next.delete(response.id);
        return next;
      });
    }
  };

  useEffect(() => {
    const currentKeys = new Set<string>();
    const toExpand: Record<string, boolean> = {};
    responses.forEach((response) => {
      const responseVacancyId = response.sourceVacancyId || vacancyId;
      const evaluationKey = `${responseVacancyId}_${response.id}`;
      const evaluation = aiEvaluations[evaluationKey];
      if (evaluation) {
        currentKeys.add(evaluationKey);
        if (!prevEvaluationKeysRef.current.has(evaluationKey)) {
          toExpand[response.id] = true;
        }
      }
    });
    prevEvaluationKeysRef.current = currentKeys;
    if (Object.keys(toExpand).length > 0) {
      setExpandedAiComment(prev => ({ ...prev, ...toExpand }));
    }
  }, [aiEvaluations, vacancyId, responses]);
  
  const { fullResumes, loadingResumeIds, loadSingleResume, failedResumeIds, viewLimitReached } = useBackgroundResumeLoader({
    responses: responses.filter((r) => (r.sourceType || r.source || '').toString().toLowerCase() !== 'avito'),
    autoLoad: true,
  });

  const viewLimitToastShownRef = useRef(false);
  useEffect(() => {
    if (!viewLimitReached) return;
    if (viewLimitToastShownRef.current) return;
    viewLimitToastShownRef.current = true;
    toast.error('Дневной лимит просмотра резюме на HH.ru исчерпан');
  }, [viewLimitReached]);

  useEffect(() => {
    const now = Date.now();
    const keys = new Set<string>();
    for (const response of responses) {
      const responseVacancyId = response.sourceVacancyId || vacancyId;
      const evaluationKey = `${responseVacancyId}_${response.id}`;
      keys.add(evaluationKey);
      const evaluation = aiEvaluations[evaluationKey];
      if (evaluation) requestedEvaluationKeysRef.current.delete(evaluationKey);

      if (!evaluation || evaluation.comment !== 'Ошибка оценки') {
        delete evalRetryAtRef.current[evaluationKey];
        delete evalRetryCountRef.current[evaluationKey];
        delete evalErrorSeenRef.current[evaluationKey];
        continue;
      }

      if (evalErrorSeenRef.current[evaluationKey]) continue;
      evalErrorSeenRef.current[evaluationKey] = true;

      const prevCount = typeof evalRetryCountRef.current[evaluationKey] === 'number' ? evalRetryCountRef.current[evaluationKey] : 0;
      const nextCount = prevCount + 1;
      evalRetryCountRef.current[evaluationKey] = nextCount;

      const baseDelayMs = 3000;
      const backoffMs = Math.min(60000, baseDelayMs * Math.pow(2, Math.max(0, nextCount - 1)));
      evalRetryAtRef.current[evaluationKey] = now + backoffMs;
    }

    Object.keys(evalRetryAtRef.current).forEach((k) => {
      if (!keys.has(k)) {
        delete evalRetryAtRef.current[k];
        delete evalRetryCountRef.current[k];
        delete evalErrorSeenRef.current[k];
        requestedEvaluationKeysRef.current.delete(k);
      }
    });
  }, [aiEvaluations, responses, vacancyId]);

  useEffect(() => {
    if (!onEvaluateResume) return;
    if (responses.length === 0) return;

    const runId = ++evaluationRunIdRef.current;
    let cancelled = false;

    const run = async () => {
      const pending: Array<{ key: string; vacancyId: string; responseId: string; resumeData: any }> = [];
      const MAX_RETRIES = 3;
      const now = Date.now();

      for (const response of responses) {
        const responseVacancyId = response.sourceVacancyId || vacancyId;
        const evaluationKey = `${responseVacancyId}_${response.id}`;
        const responseSource = (response.sourceType || response.source || response.resume?.source || '').toString().toLowerCase();
        const isAvitoResponse = responseSource === 'avito';

        if (evaluatingIds.has(evaluationKey)) continue;
        const existingEvaluation = aiEvaluations[evaluationKey];
        if (existingEvaluation && existingEvaluation.comment !== 'Ошибка оценки') continue;
        if (requestedEvaluationKeysRef.current.has(evaluationKey)) continue;
        if (existingEvaluation && existingEvaluation.comment === 'Ошибка оценки') {
          const attempts = typeof evalRetryCountRef.current[evaluationKey] === 'number' ? evalRetryCountRef.current[evaluationKey] : 0;
          const retryAt = typeof evalRetryAtRef.current[evaluationKey] === 'number' ? evalRetryAtRef.current[evaluationKey] : 0;
          if (attempts >= MAX_RETRIES) continue;
          if (retryAt && retryAt > now) continue;
        }

        const resumeId = response.resume?.id;
        if (!resumeId) continue;

        const fullResume = fullResumes[resumeId];
        const canFallback = viewLimitReached || failedResumeIds.has(resumeId);
        const resumeData =
          fullResume && fullResume._cacheMode === 'full'
            ? fullResume
            : (isAvitoResponse || canFallback ? response.resume : null);
        if (!resumeData) continue;

        pending.push({ key: evaluationKey, vacancyId: responseVacancyId, responseId: response.id, resumeData });
      }

      if (pending.length === 0) {
        setLocalQueuedEvaluations((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        return;
      }

      const nextQueued = Object.fromEntries(pending.map((p) => [p.key, true]));
      setLocalQueuedEvaluations((prev) => (sameBooleanMap(prev, nextQueued) ? prev : nextQueued));

      for (const item of pending) {
        if (cancelled) break;
        if (evaluationRunIdRef.current !== runId) break;

        setLocalQueuedEvaluations((prev) => {
          if (!prev[item.key]) return prev;
          const next = { ...prev };
          delete next[item.key];
          return next;
        });

        if (requestedEvaluationKeysRef.current.has(item.key)) continue;
        requestedEvaluationKeysRef.current.add(item.key);

        await onEvaluateResume(item.vacancyId, item.responseId, item.resumeData);
        await new Promise((resolve) => setTimeout(resolve, 350));
      }

      if (!cancelled && evaluationRunIdRef.current === runId) {
        setLocalQueuedEvaluations((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [onEvaluateResume, responses, vacancyId, aiEvaluations, evaluatingIds, fullResumes, failedResumeIds, viewLimitReached]);

  const calculateWaitingDays = (createdAt: string): number => {
    const created = new Date(createdAt);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatResponseDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long'
    }) + ' в ' + date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatExperiencePeriod = (start: string, end: string | null): string => {
    const startDate = new Date(start);
    const startMonth = startDate.toLocaleDateString('ru-RU', { month: 'long' });
    const startYear = startDate.getFullYear();
    
    if (!end) {
      return `${startMonth.charAt(0).toUpperCase() + startMonth.slice(1)} ${startYear} — по настоящее время`;
    }
    
    const endDate = new Date(end);
    const endMonth = endDate.toLocaleDateString('ru-RU', { month: 'long' });
    const endYear = endDate.getFullYear();
    
    return `${startMonth.charAt(0).toUpperCase() + startMonth.slice(1)} ${startYear} — ${endMonth} ${endYear}`;
  };

  const formatTotalExperience = (months: number): string => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    let result = '';
    if (years > 0) {
      const yearWord = years === 1 ? 'год' : years < 5 ? 'года' : 'лет';
      result += `${years} ${yearWord}`;
    }
    if (remainingMonths > 0) {
      const monthWord = remainingMonths === 1 ? 'месяц' : remainingMonths < 5 ? 'месяца' : 'месяцев';
      result += `${result ? ' ' : ''}${remainingMonths} ${monthWord}`;
    }
    return result || '0 месяцев';
  };

  const getJobSearchStatusText = (status: any): string => {
    if (!status) return '';
    const statusId = status?.id || status;
    switch (statusId) {
      case 'active_search':
        return 'Активно ищет работу';
      case 'looking_for_offers':
        return 'Рассматривает предложения';
      case 'has_job_offer':
        return 'Есть предложение о работе';
      case 'employed':
        return 'Не ищет работу';
      case 'not_looking_for_job':
        return 'Не ищет работу';
      default:
        return status?.name || '';
    }
  };

  const [actionModalInitial, setActionModalInitial] = useState<{ actionId: string; templateId: string } | null>(null);

  const openInviteModal = (response: any) => {
    const actionIds = new Set((response?.actions || []).map((action: any) => action.id));
    const employerStateId = response?.employer_state?.id || response?.state?.id;
    const preferredActionId = employerStateId === 'phone_interview' ? 'interview' : 'phone_interview';
    const fallbackActionId = preferredActionId === 'interview' ? 'phone_interview' : 'interview';
    const actionId = actionIds.has(preferredActionId)
      ? preferredActionId
      : (actionIds.has(fallbackActionId) ? fallbackActionId : preferredActionId);

    setActionModalInitial({ actionId, templateId: 'invite_after_response' });
    setActionModalResponses([response]);
  };

  const openRejectModal = (response: any) => {
    setActionModalInitial({ actionId: 'discard_by_employer', templateId: 'discard_after_response' });
    setActionModalResponses([response]);
  };

  const closeActionModal = () => {
    setActionModalResponses([]);
    setActionModalInitial(null);
  };

  const toggleResponseSelection = (responseId: string) => {
    setSelectedResponses(prev => {
      const updated = new Set(prev);
      if (updated.has(responseId)) {
        updated.delete(responseId);
      } else {
        updated.add(responseId);
      }
      return updated;
    });
  };

  const toggleLastJobExpanded = (responseId: string) => {
    setExpandedLastJob(prev => ({
      ...prev,
      [responseId]: !prev[responseId]
    }));
  };

  const toggleAiCommentExpanded = (responseId: string) => {
    setExpandedAiComment(prev => ({
      ...prev,
      [responseId]: !prev[responseId]
    }));
  };

  const getRatingBadgeStyle = (rating: number): { bg: string; text: string } => {
    if (typeof rating !== 'number') return { bg: '#E5E7EB', text: '#6B7280' };
    if (rating >= 75) return { bg: '#CBF5E5', text: '#176448' };
    if (rating >= 50) return { bg: '#FBDFB1', text: '#693D11' };
    return { bg: '#FECACA', text: '#991B1B' };
  };

  const getRatingLabel = (rating: number): string => {
    if (typeof rating !== 'number') return '';
    if (rating >= 75) return 'Высокое соответствие';
    if (rating >= 50) return 'Среднее соответствие';
    return 'Низкое соответствие';
  };

  if (responses.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        Нет откликов на эту вакансию
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {responses.map((response) => {
        const responseVacancyId = response.sourceVacancyId || vacancyId;
        const responseSource = (response.sourceType || response.source || response.resume?.source || '').toString().toLowerCase();
        const isAvitoResponse = responseSource === 'avito';
        const evaluationKey = `${responseVacancyId}_${response.id}`;
        const evaluation = aiEvaluations[evaluationKey];
        const isEvaluating = evaluatingIds.has(evaluationKey);
        const isQueued = (queuedEvaluations || {})[evaluationKey] || localQueuedEvaluations[evaluationKey];
        const isSelected = selectedResponses.has(response.id);
        const waitingDays = calculateWaitingDays(response.created_at);
        
        const resume = response.resume || {};
        const fullResume = fullResumes[resume.id];
        const lastExperience = resume.experience?.[0];
        const fullLastExperience = fullResume?.experience?.[0];
        const lastExperienceDescription = fullLastExperience?.description || lastExperience?.description;
        const isLoadingResume = loadingResumeIds.has(resume.id);
        const hasFullData = !!fullResume;
        const hasFailed = failedResumeIds.has(resume.id);
        const jobSearchStatus = response.job_search_status || fullResume?.job_search_status || resume.job_search_status;
        const age = fullResume?.age || resume.age;
        const candidateName = formatCandidateShortName({
          first_name: resume.first_name,
          last_name: resume.last_name,
          middle_name: resume.middle_name,
          fullName: resume.full_name,
          name: resume.name,
        });

        return (
          <div key={response.id} className="bg-white rounded-2xl p-6 border border-gray-200 flex gap-8 items-start">
            <div className="flex-1">
              <div className="flex flex-col gap-[6px] mb-6">
                <h3 className="text-lg font-medium text-black">
                  {resume.title || 'Без названия'}
                </h3>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-black">
                    {candidateName}
                    {age && `, ${age} ${age === 1 ? 'год' : age < 5 ? 'года' : 'лет'}`}
                    {response.created_at && ` • Откликнулся ${formatResponseDate(response.created_at)}`}
                    {resume.updated_at && (
                      <span className="text-[#2D9F75]">
                        {` • Обновлено ${new Date(resume.updated_at).toLocaleDateString('ru-RU') === new Date().toLocaleDateString('ru-RU') ? 'сегодня' : new Date(resume.updated_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`}
                      </span>
                    )}
                  </span>
                  {isAvitoResponse && (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-[#EAF4FF] text-[#2563EB]">
                      Avito
                    </span>
                  )}
                </div>
                <div className="flex flex-row items-start gap-2 flex-none">
                  <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-[#EFFAF6] text-[#2D9F75]">
                    Ждет ответа {waitingDays} {waitingDays === 1 ? 'день' : waitingDays < 5 ? 'дня' : 'дней'}
                  </span>
                  {jobSearchStatus && (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-[#EFFAF6] text-[#2D9F75]">
                      {getJobSearchStatusText(jobSearchStatus)}
                    </span>
                  )}
                  {!isAvitoResponse && !hasFullData && !isLoadingResume && resume.id && (
                    <button
                      onClick={() => loadSingleResume(resume.id)}
                      className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                        hasFailed 
                          ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                          : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                      }`}
                    >
                      {hasFailed ? 'Повторить' : 'Загрузить детали'}
                    </button>
                  )}
                  {isLoadingResume && (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-500">
                      <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Загрузка...
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {resume.total_experience && (
                  <div className="flex items-center gap-4 py-1">
                    <span className="text-sm text-[#525866] w-[180px] shrink-0">Общий опыт</span>
                    <span className="text-sm text-[#0A0D14]">
                      {formatTotalExperience(resume.total_experience.months)}
                    </span>
                  </div>
                )}
                
                <div className="h-[1px] bg-[#E2E4E9] w-full" />
                
                {lastExperience && (
                  <>
                    <div className="flex items-start gap-4 py-1">
                      <span className="text-sm text-[#525866] w-[180px] shrink-0">Последнее место работы</span>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-[#0A0D14]">
                          {lastExperience.position}
                        </span>
                        <span className="text-sm text-[#525866]">
                          {lastExperience.company} • {formatExperiencePeriod(lastExperience.start, lastExperience.end)}
                        </span>
                        {(lastExperienceDescription || isLoadingResume) && (
                          <button
                            onClick={() => toggleLastJobExpanded(response.id)}
                            className="flex items-center gap-1 text-sm text-[#375DFB] hover:underline mt-1"
                            disabled={isLoadingResume && !lastExperienceDescription}
                          >
                            {isLoadingResume && !lastExperienceDescription ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#375DFB]"></div>
                                Загрузка...
                              </>
                            ) : (
                              <>
                                {expandedLastJob[response.id] ? 'Скрыть' : 'Подробнее о последнем месте'}
                                <ChevronDownIcon className={`w-5 h-5 transition-transform ${expandedLastJob[response.id] ? 'rotate-180' : ''}`} />
                              </>
                            )}
                          </button>
                        )}
                        {expandedLastJob[response.id] && lastExperienceDescription && (
                          <p className="text-sm text mt-2 whitespace-pre-line">
                            {lastExperienceDescription}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="h-[1px] bg-[#E2E4E9] w-full" />
                  </>
                )}
                
                {resume.area && (
                  <>
                    <div className="flex items-center gap-4 py-1">
                      <span className="text-sm text-[#525866] w-[180px] shrink-0">Регион</span>
                      <span className="text-sm text-[#0A0D14]">
                        {resume.area.name}
                      </span>
                    </div>
                    <div className="h-[1px] bg-[#E2E4E9] w-full" />
                  </>
                )}
                
              </div>

              <div className="flex items-center gap-2 mt-6 flex-wrap">
                <label
                  className="inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] rounded-xl cursor-pointer transition-colors bg-[#F6F8FA] hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleResponseSelection(response.id)}
                    className="h-4 w-4 text-[#375DFB] focus:ring-[#375DFB] border-gray-300 rounded cursor-pointer"
                  />
                </label>

                {!isSelected && !isAvitoResponse && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        openInviteModal(response);
                      }}
                      className="inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100"
                    >
                      Пригласить
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        openRejectModal(response);
                      }}
                      className="inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100"
                    >
                      Отказать
                    </button>
                    <div className="relative group">
                      <button
                        type="button"
                        disabled={clarifyingIds.has(response.id) || activeResumeIds.has(resume.id)}
                        onClick={() => handleClarifyExperience(response)}
                        className={`inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl ${
                          clarifyingIds.has(response.id) || activeResumeIds.has(resume.id)
                            ? 'text-[#868C98] bg-[#F6F8FA] cursor-not-allowed opacity-60'
                            : 'text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100'
                        }`}
                      >
                        {clarifyingIds.has(response.id) ? (
                          <>
                            <svg className="animate-spin -ml-0.5 mr-1.5 h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Запуск...
                          </>
                        ) : 'Уточнить опыт'}
                      </button>
                      {activeResumeIds.has(resume.id) && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          У кандидата уже есть активная беседа
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {!isSelected && isAvitoResponse && (
                  <button
                    type="button"
                    disabled={clarifyingIds.has(response.id)}
                    onClick={() => handleAvitoClarifyExperience(response)}
                    className={`inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl ${
                      clarifyingIds.has(response.id)
                        ? 'text-[#868C98] bg-[#F6F8FA] cursor-not-allowed opacity-60'
                        : 'text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100'
                    }`}
                  >
                    {clarifyingIds.has(response.id) ? (
                      <>
                        <svg className="animate-spin -ml-0.5 mr-1.5 h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Запуск...
                      </>
                    ) : 'Уточнить опыт'}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleShowResume(response, resume)}
                  className="inline-flex items-center justify-center h-10 w-10 border border-[#E2E4E9] rounded-xl text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100"
                >
                  <span className="sr-only">Просмотреть</span>
                  <EyeIcon className="w-5 h-5" />
                </button>

              </div>
            </div>

            <div 
              className="w-[400px] rounded-xl p-3 flex-shrink-0 self-start cursor-pointer transition-all duration-200"
              style={{ backgroundColor: '#F6F8FA' }}
              onClick={() => (evaluation || (scoringCreditLimitHit && !evaluation)) && toggleAiCommentExpanded(response.id)}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {!scoringCreditLimitHit && (selectedVacancy as any)?.scoringEnabled === false && !evaluation ? (
                    <span className="text-sm text-gray-400">—</span>
                  ) : scoringCreditLimitHit && !evaluation ? (
                    <span className="text-sm text-gray-400">Оценка резюме недоступна</span>
                  ) : isEvaluating ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Анализ...
                    </div>
                  ) : isQueued ? (
                    <div className="flex items-center text-sm text-gray-400">
                      <svg className="h-4 w-4 mr-2 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      В очереди
                    </div>
                  ) : evaluation ? (
                    <>
                      <span
                        className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-sm font-medium shrink-0"
                        style={{
                          backgroundColor: getRatingBadgeStyle(evaluation.rating).bg,
                          color: getRatingBadgeStyle(evaluation.rating).text
                        }}
                      >
                        {evaluation.rating}%
                      </span>
                      <span className="text-xs font-medium text-black truncate">
                        {getRatingLabel(evaluation.rating)}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-400">Ожидание анализа</span>
                  )}
                </div>
                {(evaluation || (scoringCreditLimitHit && !evaluation)) && (
                  <img
                    src={AiCommentArrow}
                    alt=""
                    className={`w-5 h-5 shrink-0 transition-transform duration-200 ${expandedAiComment[response.id] ? 'rotate-180' : ''}`}
                  />
                )}
              </div>
              {expandedAiComment[response.id] && scoringCreditLimitHit && !evaluation && (
                <div 
                  className="overflow-hidden transition-all duration-300 ease-in-out mt-2 pt-2 border-t border-gray-200"
                >
                  <p className="text-sm text-gray-500">
                    {scoringLimitMessage}
                  </p>
                </div>
              )}
              {expandedAiComment[response.id] && evaluation && (
                <div 
                  className="overflow-hidden transition-all duration-300 ease-in-out mt-2 pt-2 border-t border-gray-200"
                >
                  {evaluation.positiveFacts && evaluation.positiveFacts.length > 0 && (
                    <ul className="space-y-1.5">
                      {evaluation.positiveFacts.map((fact: string, index: number) => (
                        <li
                          key={`pos-${index}`}
                          className="flex items-start gap-2 text-xs text-green-700 leading-[16px]"
                        >
                          <img src={AiFactPositive} alt="" className="mt-0.5 h-5 w-5 flex-shrink-0" />
                          <span className="break-words">{fact}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {evaluation.negativeFacts && evaluation.negativeFacts.length > 0 && (
                    <ul className="space-y-1.5 mt-2">
                      {evaluation.negativeFacts.map((fact: string, index: number) => (
                        <li
                          key={`neg-${index}`}
                          className="flex items-start gap-2 text-xs text-red-700 leading-[16px]"
                        >
                          <img src={AiFactNegative} alt="" className="mt-0.5 h-5 w-5 flex-shrink-0" />
                          <span className="break-words">{fact}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {evaluation.comment && !evaluation.positiveFacts?.length && !evaluation.negativeFacts?.length && (
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {evaluation.comment}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <ResponseActionModal
        isOpen={actionModalResponses.length > 0}
        onClose={closeActionModal}
        initialActionId={actionModalInitial?.actionId ?? null}
        initialTemplateId={actionModalInitial?.templateId ?? null}
        responses={actionModalResponses}
        vacancyId={vacancyId}
        positionId={(selectedVacancy as any)?.id ?? null}
        positionSlug={(selectedVacancy as any)?.publicSlug ?? null}
        aiScreeningEnabled={Boolean((selectedVacancy as any)?.aiScreeningEnabled)}
        onSuccess={closeActionModal}
        onRemoveResponses={(responseIds: string[]) => {
          // Находим resumeId кандидатов из actionModalResponses и добавляем в activeResumeIds
          const removedResumeIds = actionModalResponses
            .filter((r: any) => responseIds.includes(r.id))
            .map((r: any) => r.resume?.id)
            .filter((id: string | undefined): id is string => !!id);
          if (removedResumeIds.length > 0) {
            setActiveResumeIds(prev => {
              const next = new Set(prev);
              removedResumeIds.forEach((id: string) => next.add(id));
              return next;
            });
          }
          onRemoveResponses?.(responseIds);
        }}
      />

      <HHResumeModal
        open={hhResumeModalOpen}
        onClose={() => {
          setHhResumeModalOpen(false);
          setSelectedHHResume(null);
        }}
        resumeData={selectedHHResume}
        searchKeywords={[
          selectedVacancy?.title,
          ...(selectedVacancy?.topics || [])
        ].map((v: any) => String(v ?? '').trim()).filter(Boolean)}
      />
      <AvitoResumeModal
        open={avitoResumeModalOpen}
        onClose={() => {
          setAvitoResumeModalOpen(false);
          setSelectedAvitoResume(null);
        }}
        resumeData={selectedAvitoResume}
        searchKeywords={[
          selectedVacancy?.title,
          ...(selectedVacancy?.topics || [])
        ].map((v: any) => String(v ?? '').trim()).filter(Boolean)}
      />
      <CampaignCheckResultsModal
        open={singleCheckResultsModalOpen}
        onClose={handleSingleCheckCancel}
        onConfirm={handleSingleCheckConfirm}
        loading={Boolean(pendingSingleCampaign && clarifyingIds.has(pendingSingleCampaign.responseId))}
        checkResult={singleCheckResult}
        message={singleCheckResultMessage}
        messageType="warning"
      />
      {estaffLinkGuardModal}
    </div>
  );
};

export default VacancyResponsesInline;
